/**
 * PhoneInput — React (web) implementation.
 *
 * One field shell: a fixed "+1", a short divider, then a floating-label input.
 * The label sits in the field as a placeholder while empty and unfocused, and
 * floats to a caption above the value once focused or filled. Digits are
 * formatted as typed; `onChangeText` receives the bare national digits.
 * Disabled stays focusable (read-only plus aria-disabled), so unlike a native
 * `disabled` field it is still sent when a plain HTML form posts.
 * All styling references the shared constants in PhoneInput.ts.
 */

import React, { useId, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import { FieldError } from '../FieldError/FieldError.web';
import { hasFieldErrorMessage } from '../FieldError/FieldError';
import {
  applyPhoneInputEdit,
  formatPhoneNational,
  isPhoneInputLabelFloating,
  phoneCaretIndex,
  phoneInputColorsFor,
  phoneNationalDigits,
  PHONE_INPUT_PREFIX,
  PHONE_INPUT_HEIGHT,
  PHONE_INPUT_BORDER_RADIUS,
  PHONE_INPUT_BORDER_WIDTH,
  PHONE_INPUT_HORIZONTAL_PADDING,
  PHONE_INPUT_SHELL_GAP,
  PHONE_INPUT_LABEL_VALUE_GAP,
  PHONE_INPUT_ERROR_GAP,
  PHONE_INPUT_ERROR_INDENT,
  PHONE_INPUT_DIVIDER_WIDTH,
  PHONE_INPUT_DIVIDER_HEIGHT,
  PHONE_INPUT_VALUE_TYPOGRAPHY,
  PHONE_INPUT_RESTING_LABEL_TYPOGRAPHY,
  PHONE_INPUT_FLOATING_LABEL_TYPOGRAPHY,
} from './PhoneInput';
import type { PhoneInputDeleteDirection } from './PhoneInput';
import type { TypeStyle } from '../../elements/typography';

const PHONE_INPUT_FOCUS_STYLE_ID = 'galapagos-phone-input-focus-visible';
const PHONE_INPUT_FIELD_SELECTOR = '[data-galapagos-phone-input-field="true"]';

/** Where the caret goes after the next render: after a digit count, or at an index. */
type PendingCaret = { digits: number } | { index: number };

// The ring is drawn on the field shell, not the bare <input>, so it wraps the
// prefix too (as v7 draws it). `:has(:focus-visible)` keeps it keyboard-only
// (ADR-025 Rule 2). The input's own outline is only removed where `:has()` is
// supported, so an older browser still shows its native focus outline.
function ensurePhoneInputFocusStyle(): void {
  ensureStyle(
    PHONE_INPUT_FOCUS_STYLE_ID,
    `@supports selector(:has(*)) {` +
      ` ${PHONE_INPUT_FIELD_SELECTOR} input:focus { outline: none; }` +
      ` ${PHONE_INPUT_FIELD_SELECTOR}:has(:focus-visible) { ${FOCUS_RING_OUTLINE} }` +
      ` }`,
  );
}

function textStyle(type: TypeStyle, color: string): React.CSSProperties {
  return {
    fontFamily: type.fontFamily,
    fontWeight: type.fontWeight,
    fontSize: type.fontSize,
    lineHeight: `${type.lineHeight}px`,
    letterSpacing: type.letterSpacing,
    color,
  };
}

export interface WebPhoneInputProps {
  label: string;
  /** Controlled national digits (e.g. "2025550143"). */
  value?: string;
  /** Initial national digits when uncontrolled. */
  defaultValue?: string;
  errorText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  /** Domain action id for the change interaction (ADR-081 Rule 4). */
  actionId?: string;
  /** Receives the bare national digits, at most 10. */
  onChangeText?: (digits: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function PhoneInput({
  label,
  value,
  defaultValue,
  errorText,
  disabled = false,
  autoFocus = false,
  name,
  actionId = 'ui.phone-input.change',
  onChangeText,
  onBlur,
  onFocus,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebPhoneInputProps) {
  ensurePhoneInputFocusStyle();

  const generatedId = useId();
  const inputId = testId ? `${testId}-input` : `phone-input-${generatedId}`;
  const prefixId = `${inputId}-prefix`;
  const errorId = `${inputId}-error`;

  const [ownDigits, setOwnDigits] = useState(() => phoneNationalDigits(defaultValue ?? ''));
  const digits = value !== undefined ? phoneNationalDigits(value) : ownDigits;
  const display = formatPhoneNational(digits);

  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<PendingCaret | null>(null);
  // Set by Backspace or Delete, so only a delete-key edit triggers the
  // formatting-character rule in applyPhoneInputEdit.
  const deleteDirection = useRef<PhoneInputDeleteDirection | undefined>(undefined);
  // Bumped on every edit, so the caret is restored even when an edit is
  // rejected (a letter typed mid-number) and the display does not change.
  const [editCount, countEdit] = useReducer((count: number) => count + 1, 0);
  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (pending === null || !inputRef.current) return;
    const index = 'index' in pending ? pending.index : phoneCaretIndex(display, pending.digits);
    pendingCaret.current = null;
    if (document.activeElement === inputRef.current) {
      inputRef.current.setSelectionRange(index, index);
    }
  }, [display, editCount]);

  const hasError = hasFieldErrorMessage(errorText);
  const floating = isPhoneInputLabelFloating(digits, focused);
  const colors = phoneInputColorsFor({
    disabled,
    hasError,
    focused: focused && !disabled,
    hovered: hovered && !disabled,
  });

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (disabled) return;
    const nextText = event.target.value;
    const caret = event.target.selectionStart ?? nextText.length;
    const edit = applyPhoneInputEdit(display, nextText, caret, deleteDirection.current);
    deleteDirection.current = undefined;
    // Typing or pasting that changed nothing puts the caret back at its own
    // index, even beside a bracket or space that counting digits would skip.
    const grew = nextText.length - display.length;
    pendingCaret.current =
      edit.display === display && grew > 0
        ? { index: caret - grew }
        : { digits: edit.digitsBeforeCaret };
    countEdit();
    if (value === undefined) setOwnDigits(edit.digits);
    onChangeText?.(edit.digits);
  };

  // A press anywhere on the shell (prefix, label) lands on the input.
  const focusInput: React.MouseEventHandler<HTMLDivElement> = () => {
    inputRef.current?.focus();
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: PHONE_INPUT_SHELL_GAP,
    height: PHONE_INPUT_HEIGHT,
    boxSizing: 'border-box',
    paddingLeft: PHONE_INPUT_HORIZONTAL_PADDING,
    paddingRight: PHONE_INPUT_HORIZONTAL_PADDING,
    backgroundColor: colors.background,
    border: `${PHONE_INPUT_BORDER_WIDTH}px solid ${colors.border}`,
    borderRadius: PHONE_INPUT_BORDER_RADIUS,
    cursor: disabled ? 'not-allowed' : 'text',
  };

  const dividerStyle: React.CSSProperties = {
    flexShrink: 0,
    width: PHONE_INPUT_DIVIDER_WIDTH,
    height: PHONE_INPUT_DIVIDER_HEIGHT,
    backgroundColor: colors.divider,
  };

  const textColumnStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: PHONE_INPUT_LABEL_VALUE_GAP,
    flexGrow: 1,
    minWidth: 0,
    alignSelf: 'stretch',
  };

  const inputStyle: React.CSSProperties = {
    ...textStyle(PHONE_INPUT_VALUE_TYPOGRAPHY, colors.value),
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    width: '100%',
    cursor: 'inherit',
    // While the label stands in as the placeholder, the input still covers the
    // text column (so it stays the click and focus target) but is invisible.
    ...(floating ? {} : { position: 'absolute', inset: 0, height: '100%', opacity: 0 }),
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: PHONE_INPUT_ERROR_GAP }}
      data-testid={testId ? `${testId}-container` : undefined}
    >
      {/* analytics-exempt: forwarded-wrapper */}
      <div
        style={fieldStyle}
        data-galapagos-phone-input-field="true"
        data-testid={testId ? `${testId}-field` : undefined}
        onClick={focusInput}
        // Mouse only: a touch tap fires pointerenter too, and the hover state
        // would stick after the finger lifts.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
      >
        <span
          id={prefixId}
          style={{ ...textStyle(PHONE_INPUT_VALUE_TYPOGRAPHY, colors.prefix), flexShrink: 0 }}
          data-testid={testId ? `${testId}-prefix` : undefined}
        >
          {PHONE_INPUT_PREFIX}
        </span>
        <span aria-hidden="true" style={dividerStyle} />
        <div style={textColumnStyle}>
          <span
            aria-hidden="true"
            style={textStyle(
              floating
                ? PHONE_INPUT_FLOATING_LABEL_TYPOGRAPHY
                : PHONE_INPUT_RESTING_LABEL_TYPOGRAPHY,
              colors.label,
            )}
            data-testid={testId ? `${testId}-label` : undefined}
          >
            {label}
          </span>
          <input
            ref={inputRef}
            data-analytics-action={actionId}
            id={inputId}
            name={name ?? inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={display}
            // Disabled stays focusable (ADR-025 Rule 3): read-only plus
            // aria-disabled instead of the native `disabled` attribute.
            readOnly={disabled}
            autoFocus={autoFocus}
            onChange={handleChange}
            onKeyDown={(event) => {
              deleteDirection.current =
                event.key === 'Backspace'
                  ? 'backward'
                  : event.key === 'Delete'
                    ? 'forward'
                    : undefined;
            }}
            onFocus={() => {
              setFocused(true);
              onFocus?.();
            }}
            onBlur={() => {
              setFocused(false);
              onBlur?.();
            }}
            style={inputStyle}
            aria-label={(ariaLabel ?? label) || undefined}
            aria-invalid={hasError}
            aria-disabled={disabled || undefined}
            aria-describedby={hasError ? `${prefixId} ${errorId}` : prefixId}
            data-testid={testId}
          />
        </div>
      </div>
      {/* Always present, so the error is announced when it appears inside it. */}
      <div aria-live="polite" style={{ paddingLeft: PHONE_INPUT_ERROR_INDENT }}>
        <FieldError
          live={false}
          message={errorText}
          id={errorId}
          data-testid={testId ? `${testId}-error` : undefined}
        />
      </div>
    </div>
  );
}
